import React, { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, ShieldCheck, Check, Stethoscope, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export const REASONS = [
  ["allergy", "Allergy"], ["isotretinoin", "Isotretinoin / Roaccutane"],
  ["pregnancy", "Pregnancy / breastfeeding"], ["blood_thinner", "Blood thinners"],
  ["adverse_reaction", "Previous adverse reaction"], ["medical_clearance", "Medical clearance required"],
  ["steroids", "Anabolic steroid use"], ["metabolic", "Metabolic / hormone disorder (PCOS/Endo)"],
  ["other", "Other / manual"],
];
const REASON_LABEL = Object.fromEntries(REASONS);
const fmt = (s) => (s ? new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

// compact=true -> banner only (for calendar popup / chart); manage=false hides add/delete
export const MedicalAlerts = ({ clientId, bookingId = "", compact = false, manage = true, onChanged }) => {
  const { isAdmin } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [reason, setReason] = useState("other");
  const [note, setNote] = useState("");

  const load = () => api.get(`/clients/${clientId}/alerts`).then(({ data }) => { setAlerts(data); onChanged && onChanged(data); }).catch(() => setAlerts([]));
  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId]);

  const active = alerts.filter((a) => a.status === "active" || a.status === "needs_clearance");
  const ackedForBooking = (a) => bookingId && (a.acknowledgements || []).some((k) => k.booking_id === bookingId);

  const ackAll = async () => {
    const pending = active.filter((a) => !ackedForBooking(a));
    try { await Promise.all(pending.map((a) => api.post(`/alerts/${a.id}/acknowledge`, { booking_id: bookingId }))); toast.success(`${pending.length} alerts acknowledged — you can complete the sale`); load(); }
    catch { toast.error("Couldn't acknowledge"); load(); }
  };
  const acknowledge = async (a) => { try { await api.post(`/alerts/${a.id}/acknowledge`, { booking_id: bookingId }); toast.success("Alert acknowledged"); load(); } catch { toast.error("Couldn't acknowledge"); } };
  const setStatus = async (a, status) => { try { await api.put(`/alerts/${a.id}`, { status }); load(); } catch { toast.error("Couldn't update"); } };
  const del = async (a) => { if (!window.confirm("Delete this alert?")) return; try { await api.delete(`/alerts/${a.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); } };
  const add = async () => { try { await api.post(`/clients/${clientId}/alerts`, { reason, note }); setAdding(false); setNote(""); setReason("other"); toast.success("Alert added"); load(); } catch { toast.error("Couldn't add"); } };

  if (compact) {
    if (active.length === 0) return null;
    return (
      <div className="rounded-xl border-2 border-red-400 bg-red-50 p-3 space-y-2 min-w-0 overflow-hidden" data-testid="medical-alert-banner">
        <div className="flex items-center gap-2 text-red-700 font-semibold text-sm min-w-0"><AlertTriangle className="h-4 w-4 shrink-0" /><span className="flex-1 min-w-0 truncate">Medical alerts — acknowledge before treatment</span>
          {bookingId && active.filter((a) => !ackedForBooking(a)).length > 1 && (
            <Button size="sm" variant="outline" className="h-7 rounded-lg border-red-400 text-red-700 hover:bg-red-100 shrink-0" onClick={ackAll} data-testid="alert-ack-all">Acknowledge all ({active.filter((a) => !ackedForBooking(a)).length})</Button>
          )}
        </div>
        {active.map((a) => (
          <div key={a.id} className="flex items-center gap-2 text-sm min-w-0" data-testid={`alert-item-${a.reason}`}>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 max-w-[45%] truncate ${a.status === "needs_clearance" ? "bg-amber-200 text-amber-800" : "bg-red-200 text-red-800"}`} title={a.label}>{a.label}</span>
            <span className="text-red-900/70 text-xs flex-1 min-w-0 truncate" title={a.note}>{a.note}</span>
            {bookingId && (ackedForBooking(a)
              ? <span className="text-emerald-700 text-xs flex items-center gap-1 shrink-0"><Check className="h-3.5 w-3.5" />Acknowledged</span>
              : <Button size="sm" className="h-7 rounded-lg bg-red-600 hover:bg-red-700 shrink-0" onClick={() => acknowledge(a)} data-testid={`alert-ack-${a.reason}`} data-alert-id={a.id}>Acknowledge</Button>)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="medical-alerts">
      {active.length > 0 && (
        <div className="rounded-xl border-2 border-red-400 bg-red-50 p-3 space-y-2">
          <div className="flex items-center gap-2 text-red-700 font-semibold text-sm"><AlertTriangle className="h-4 w-4" />Active medical alerts ({active.length})</div>
          {active.map((a) => (
            <div key={a.id} className="rounded-lg bg-white/70 border border-red-200 p-2.5" data-testid={`alert-card-${a.id}`}>
              <div className="flex items-start gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${a.status === "needs_clearance" ? "bg-amber-200 text-amber-800" : "bg-red-200 text-red-800"}`}>{a.label}</span>
                <div className="flex-1 min-w-0">
                  {a.note && <div className="text-xs text-red-900/80">{a.note}</div>}
                  <div className="text-[10px] text-muted-foreground mt-0.5">{a.source === "form" ? "From consultation form" : a.source === "toggle" ? "Client flag" : `Added by ${a.created_by}`} · {fmt(a.created_at)}
                    {(a.acknowledgements || []).length > 0 && <span className="text-emerald-700"> · Last ack by {a.acknowledgements[a.acknowledgements.length - 1].by}</span>}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {bookingId && !ackedForBooking(a) && <Button size="sm" className="h-7 rounded-lg bg-red-600 hover:bg-red-700" onClick={() => acknowledge(a)} data-testid={`alert-ack-${a.id}`}><Check className="h-3.5 w-3.5 mr-1" />Acknowledge</Button>}
                {manage && a.status !== "needs_clearance" && <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={() => setStatus(a, "needs_clearance")} data-testid={`alert-clearance-${a.id}`}><ShieldCheck className="h-3.5 w-3.5 mr-1" />Needs clearance</Button>}
                {manage && <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={() => setStatus(a, "resolved")} data-testid={`alert-resolve-${a.id}`}>Resolve</Button>}
                {manage && isAdmin && <Button size="sm" variant="ghost" className="h-7 rounded-lg text-destructive" onClick={() => del(a)} data-testid={`alert-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {manage && (
        <>
          {alerts.filter((a) => a.status === "resolved").length > 0 && (
            <details className="rounded-lg border border-border p-2 text-sm" data-testid="alerts-resolved">
              <summary className="cursor-pointer text-muted-foreground text-xs">Resolved alerts ({alerts.filter((a) => a.status === "resolved").length})</summary>
              <div className="mt-2 space-y-1">
                {alerts.filter((a) => a.status === "resolved").map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{a.label} · {a.note}</span>
                    {isAdmin && <button onClick={() => del(a)} className="hover:text-destructive"><X className="h-3.5 w-3.5" /></button>}
                  </div>
                ))}
              </div>
            </details>
          )}
          {adding ? (
            <div className="rounded-lg border border-border p-3 space-y-2" data-testid="alert-add-form">
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-9 rounded-lg" data-testid="alert-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Details (e.g. penicillin allergy)" value={note} onChange={(e) => setNote(e.target.value)} className="h-9 rounded-lg" data-testid="alert-note" />
              <div className="flex gap-2"><Button size="sm" className="rounded-lg" onClick={add} data-testid="alert-add-save">Add alert</Button><Button size="sm" variant="outline" className="rounded-lg" onClick={() => setAdding(false)}>Cancel</Button></div>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setAdding(true)} data-testid="add-alert-button"><Plus className="h-4 w-4 mr-1" />Add medical alert</Button>
          )}
        </>
      )}
    </div>
  );
};

export const MedicalHistory = ({ clientId }) => {
  const [data, setData] = useState(null);
  useEffect(() => { if (clientId) api.get(`/clients/${clientId}/medical-history`).then(({ data }) => setData(data)).catch(() => {}); }, [clientId]);
  if (!data) return null;
  const { entries = [], flags = [] } = data;
  if (entries.length === 0 && flags.length === 0) return <p className="text-sm text-muted-foreground">No medical history captured yet. It builds automatically from submitted consultation forms.</p>;
  return (
    <div className="space-y-2" data-testid="medical-history">
      <div className="flex items-center gap-1.5 text-sm font-medium text-primary"><Stethoscope className="h-4 w-4" />Medical history summary</div>
      {flags.length > 0 && <div className="flex flex-wrap gap-1.5">{flags.map((f) => <span key={f} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">{f}</span>)}</div>}
      <div className="rounded-lg border border-border divide-y divide-border/60">
        {entries.map((e, i) => (
          <div key={i} className="p-2.5 text-sm" data-testid={`mh-entry-${i}`}>
            <div className="text-xs text-muted-foreground">{e.label}</div>
            <div className="font-medium">{e.value}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">from “{e.source}” · {fmt(e.at)}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">Built from submitted forms — the original completed forms stay unchanged in the Forms tab.</p>
    </div>
  );
};
